import { computed, getCurrentInstance, ref, watch } from 'vue';

import logger from '@/services/logger';
import { fetchVendorCapacitySettings } from '@/services/vendorService';
import type { CapacityType } from '@/types/capacityType';
import { extractErrorMessage } from '@/utils/apiError';

// Manual capacity entry must only offer types actually configured
// for the selected vendor (the Vendor Settings page's vendor_capacity_settings
// rows) — otherwise the form silently creates capacity data for a type/vendor
// combination the vendor was never set up for.
export function useVendorAvailableCapacityTypes(vendorId: () => string, allTypes: () => CapacityType[]) {
  const configuredTypeIds = ref<Set<string>>(new Set());
  const loading = ref(false);

  async function reload(): Promise<void> {
    const id = vendorId();
    if (!id) {
      configuredTypeIds.value = new Set();
      return;
    }
    loading.value = true;
    try {
      const settings = await fetchVendorCapacitySettings(id);
      configuredTypeIds.value = new Set(settings.map((setting) => setting.capacityTypeId));
    } catch (error) {
      configuredTypeIds.value = new Set();
      logger.showError(extractErrorMessage(error, "Failed to load vendor's capacity types"));
    } finally {
      loading.value = false;
    }
  }

  const availableTypes = computed(() => allTypes().filter((type) => configuredTypeIds.value.has(type.id)));

  if (getCurrentInstance()) {
    watch(vendorId, () => void reload(), { immediate: true });
  }

  return { availableTypes, loading, reload };
}
